import { extractQueryState } from "@shared/utils/extractQueryState";
import { useQuery } from "@tanstack/react-query";
import { getUsersApiClient } from "@shared/apis/sidekick/api-factories";
import { GetUserResponse } from "@shared/apis/sidekick/generated";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { toast } from "@shared/services/toast";

type Props = {
  id: string;
  onSuccess?: (data: GetUserResponse) => void;
  onError?: () => void;
};

const CACHE_TIME_IN_MS = 1000 * 60 * 5; // 5 minutes

export const buildGetUserQueryKey = (id: string) => {
  return ["user", { id }];
};

export const useGetUser = (params: Props) => {
  const { id, onSuccess, onError } = params || {};

  const api = getUsersApiClient();
  const query = useQuery({
    queryKey: buildGetUserQueryKey(id),
    queryFn: async () => {
      try {
        const data = await api.userControllerGetUser({
          userId: id,
        });
        onSuccess?.(data);
        return data;
      } catch (error) {
        const apiError = await apiResponseErrorHandler(error);
        console.error(`Failed to get user with id: ${id}`, error);
        onError?.();

        if (apiError.status === 404) {
          toast.error(`User not found with id: ${id}.`, {
            toastId: "user-not-found-error",
          });
          throw apiError;
        }

        toast.error("Error trying to get user information.", {
          toastId: "user-unexpected-error",
        });
        throw apiError;
      }
    },
    enabled: !!id,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1,
    gcTime: CACHE_TIME_IN_MS,
    staleTime: CACHE_TIME_IN_MS,
  });

  return extractQueryState(query);
};
